import { useQuery } from '@apollo/client/react/hooks';
import {
  generatePath,
  useLocation,
  useParams,
  useRouteMatch,
  useHistory,
} from 'react-router-dom';
import { parse } from 'querystring';
import {
  parseQueryString,
  parseRoute,
} from '@app/components/ProductFinder/url';
import getMenuOption from '@app/hooks/useMenuConfig/getMenuOption';
import { getIntlValidLocale } from '@app/helpers/locale';
import buildQueryCondition from '@app/hooks/useMenuConfig/buildQueryCondition';
import menuQueryConfig from './query';
import { MenuConfigHookOut, MenuConfigLoadingState } from './types';

const redirectToRoot = (match, history): void => {
  const newPath = generatePath(match.path, {
    language: match.params.language,
    url: '',
  });
  history.replace(newPath);
};

export default function useMenuConfig(
  variant: string = ''
): MenuConfigHookOut | MenuConfigLoadingState {
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();
  const { url, language } = useParams<{ url: string; language: string }>();
  const { previewToken } = parse(location.search?.substring(1));
  const filters = [];
  let menuSlugs = [''];

  const { loading, data } = useQuery(menuQueryConfig, {
    variables: {
      language: getIntlValidLocale(language),
      variant,
      previewToken,
    },
  });

  if (loading) {
    return { loading, menuSlugs, filters };
  }

  const { menu, sortingValues } = data ?? {};

  menuSlugs = parseRoute(url);
  let menuOption = getMenuOption(menu, menuSlugs);

  if (!menuOption) {
    // look for menu option in parent's slug
    menuSlugs = ['', ...menuSlugs];
    menuOption = getMenuOption(menu, menuSlugs);
  }

  if (!menuOption) {
    redirectToRoot(match, history);
    return { loading: true, menuSlugs, filters };
  }

  const searchState = parseQueryString(filters, location.search);
  const { query: menuQuery, protected: isPageProtected } = menuOption;
  const productsCondition = Object.keys(searchState.values).length
    ? {
        and: ([] as object[])
          .concat(menuQuery || [])
          .concat(buildQueryCondition(filters, searchState.values)),
      }
    : menuQuery;

  return {
    loading,
    menuSlugs,
    filters,
    language,
    menu,
    isPageProtected,
    sortingValues,
    searchState,
    productsCondition,
  };
}
